import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { AuthProvider as MediaAuthProvider } from '@atlaskit/media-core';

import { cacheWithExp } from '@confluence/fabric-media-support/entry-points/cacheWithExp.ts';
import { getAuthFromContext } from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { fg } from '@confluence/feature-gating';

import {
	fetchSnippetMediaToken,
	type SnippetMediaAuth,
} from '@confluence/fabric-media-support/entry-points/fetchSnippetMediaToken.ts';
import { SnippetScope } from '@confluence/fabric-media-support/entry-points/snippetScope.ts';

export type SnippetsMediaProviderOptions = {
	snippetId: string;
	scope?: SnippetScope;
};

const cachedFetchSnippetMediaToken = cacheWithExp<SnippetMediaAuth>(
	(snippetId: string) => fetchSnippetMediaToken(snippetId),
	(auth) => auth.token,
);

const createSnippetsAuthProvider =
	(snippetMediaAuth: SnippetMediaAuth): MediaAuthProvider =>
	async () => {
		return {
			token: snippetMediaAuth.token,
			clientId: snippetMediaAuth.clientId,
			baseUrl: snippetMediaAuth.baseUrl,
		};
	};

export const createSnippetsRendererMediaProvider = async (
	options: SnippetsMediaProviderOptions,
): Promise<MediaProvider> => {
	const { snippetId, scope } = options;

	if (scope === SnippetScope.GLOBAL) {
		return {
			viewMediaClientConfig: {
				authProvider: getAuthFromContext as unknown as MediaAuthProvider,
				getAuthFromContext,
			},
		};
	}

	const snippetMediaAuth = await cachedFetchSnippetMediaToken.getValue(snippetId);
	const authProvider = createSnippetsAuthProvider(snippetMediaAuth);

	return {
		viewMediaClientConfig: {
			authProvider,
			getAuthFromContext,
		},
	};
};

export const createSnippetsEditorMediaProvider = async (
	options: SnippetsMediaProviderOptions,
): Promise<MediaProvider> => {
	const { snippetId } = options;
	const snippetMediaAuth = await cachedFetchSnippetMediaToken.getValue(snippetId);
	const authProvider = createSnippetsAuthProvider(snippetMediaAuth);

	return {
		viewMediaClientConfig: {
			authProvider,
			getAuthFromContext,
		},
		uploadMediaClientConfig: {
			authProvider,
			getAuthFromContext,
			useSha256ForUploads: true,
		},
		viewAndUploadMediaClientConfig: fg('platform_media_video_captions')
			? { authProvider, getAuthFromContext }
			: undefined,
		uploadParams: {
			collection: snippetMediaAuth.collectionId,
		},
	};
};
